/**
 * Automation Data Model and Utilities
 * Handles automation lanes, points, and curve interpolation
 */

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Automation parameter types (track-level)
export const AUTOMATION_PARAMS = {
  volume: { label: 'Volume', min: 0, max: 1, default: 0.8, unit: '%', displayMultiplier: 100 },
  pan: { label: 'Pan', min: -1, max: 1, default: 0, unit: '', displayFormatter: (v) => v === 0 ? 'C' : v < 0 ? `L${Math.abs(Math.round(v * 100))}` : `R${Math.round(v * 100)}` },
  eqBass: { label: 'EQ Bass', min: -12, max: 12, default: 0, unit: 'dB' },
  eqMid: { label: 'EQ Mid', min: -12, max: 12, default: 0, unit: 'dB' },
  eqTreble: { label: 'EQ Treble', min: -12, max: 12, default: 0, unit: 'dB' },
  reverb: { label: 'Reverb', min: 0, max: 1, default: 0, unit: '%', displayMultiplier: 100 },
  delay: { label: 'Delay', min: 0, max: 1, default: 0, unit: '%', displayMultiplier: 100 },
};

// Plugin parameter configs for automation
export const PLUGIN_AUTOMATION_PARAMS = {
  // Compressor params
  'compressor.threshold': { label: 'Comp: Threshold', min: -60, max: 0, default: -24, unit: 'dB' },
  'compressor.ratio': { label: 'Comp: Ratio', min: 1, max: 20, default: 4, unit: ':1' },
  'compressor.attack': { label: 'Comp: Attack', min: 0, max: 1, default: 0.003, unit: 's', displayMultiplier: 1000, displaySuffix: 'ms' },
  'compressor.release': { label: 'Comp: Release', min: 0.01, max: 1, default: 0.25, unit: 's', displayMultiplier: 1000, displaySuffix: 'ms' },
  'compressor.makeup': { label: 'Comp: Makeup', min: -12, max: 24, default: 0, unit: 'dB' },
  'compressor.mix': { label: 'Comp: Mix', min: 0, max: 1, default: 1, unit: '%', displayMultiplier: 100 },
  
  // EQ params
  'parametric-eq.lowGain': { label: 'EQ: Low Gain', min: -18, max: 18, default: 0, unit: 'dB' },
  'parametric-eq.mid1Gain': { label: 'EQ: Mid1 Gain', min: -18, max: 18, default: 0, unit: 'dB' },
  'parametric-eq.mid2Gain': { label: 'EQ: Mid2 Gain', min: -18, max: 18, default: 0, unit: 'dB' },
  'parametric-eq.highGain': { label: 'EQ: High Gain', min: -18, max: 18, default: 0, unit: 'dB' },
  'parametric-eq.lowFreq': { label: 'EQ: Low Freq', min: 20, max: 500, default: 100, unit: 'Hz' },
  'parametric-eq.mid1Freq': { label: 'EQ: Mid1 Freq', min: 100, max: 2000, default: 400, unit: 'Hz' },
  'parametric-eq.mid2Freq': { label: 'EQ: Mid2 Freq', min: 500, max: 8000, default: 2500, unit: 'Hz' },
  'parametric-eq.highFreq': { label: 'EQ: High Freq', min: 2000, max: 16000, default: 8000, unit: 'Hz' },
  
  // Reverb params
  'reverb.decay': { label: 'Reverb: Decay', min: 0.1, max: 10, default: 2, unit: 's' },
  'reverb.mix': { label: 'Reverb: Mix', min: 0, max: 1, default: 0.3, unit: '%', displayMultiplier: 100 },
  'reverb.damping': { label: 'Reverb: Damping', min: 1000, max: 16000, default: 8000, unit: 'Hz', displayMultiplier: 0.001, displaySuffix: 'kHz' },
  'reverb.size': { label: 'Reverb: Size', min: 0.1, max: 1, default: 0.5, unit: '%', displayMultiplier: 100 },
  'reverb.preDelay': { label: 'Reverb: Pre-Delay', min: 0, max: 0.2, default: 0.02, unit: 's', displayMultiplier: 1000, displaySuffix: 'ms' },
  
  // Delay params
  'delay.time': { label: 'Delay: Time', min: 0.01, max: 2, default: 0.3, unit: 's', displayMultiplier: 1000, displaySuffix: 'ms' },
  'delay.feedback': { label: 'Delay: Feedback', min: 0, max: 0.95, default: 0.4, unit: '%', displayMultiplier: 100 },
  'delay.mix': { label: 'Delay: Mix', min: 0, max: 1, default: 0.3, unit: '%', displayMultiplier: 100 },
  'delay.filter': { label: 'Delay: Filter', min: 500, max: 12000, default: 6000, unit: 'Hz', displayMultiplier: 0.001, displaySuffix: 'kHz' },
  
  // Chorus params
  'chorus.rate': { label: 'Chorus: Rate', min: 0.1, max: 5, default: 0.5, unit: 'Hz' },
  'chorus.depth': { label: 'Chorus: Depth', min: 0, max: 0.01, default: 0.002, unit: '%', displayMultiplier: 10000 },
  'chorus.mix': { label: 'Chorus: Mix', min: 0, max: 1, default: 0.5, unit: '%', displayMultiplier: 100 },
  
  // Gain params
  'gain.gain': { label: 'Gain: Level', min: -48, max: 24, default: 0, unit: 'dB' },
  'gain.pan': { label: 'Gain: Pan', min: -1, max: 1, default: 0, unit: '', displayFormatter: (v) => v === 0 ? 'C' : v < 0 ? `L${Math.abs(Math.round(v * 100))}` : `R${Math.round(v * 100)}` },
};

// Get all automation params (track + plugin)
export const getAllAutomationParams = () => ({
  ...AUTOMATION_PARAMS,
  ...PLUGIN_AUTOMATION_PARAMS,
});

// Get automatable parameters for a specific plugin type
export const getPluginAutomationParams = (pluginType) => {
  const prefix = pluginType.toLowerCase() + '.';
  const params = {};
  
  Object.entries(PLUGIN_AUTOMATION_PARAMS).forEach(([key, config]) => {
    if (key.startsWith(prefix)) {
      params[key] = config;
    }
  });
  
  return params;
};

// Check if a parameter is a plugin parameter
export const isPluginParameter = (paramKey) => {
  return paramKey.includes('.');
};

// Parse plugin parameter key into plugin type and param name
export const parsePluginParamKey = (paramKey) => {
  const [pluginType, paramName] = paramKey.split('.');
  return { pluginType, paramName };
};

/**
 * Get available automation parameters for a track based on its plugins
 * Returns categorized params: track-level and per-plugin
 */
export const getTrackAutomationParams = (trackPlugins = []) => {
  // Track-level parameters are always available
  const result = {
    track: { ...AUTOMATION_PARAMS },
    plugins: {},
  };
  
  // Add parameters for each plugin on the track
  trackPlugins.forEach((plugin, index) => {
    // Use plugin name (lowercased) to look up parameters
    // e.g., "Compressor" -> "compressor", "Parametric EQ" -> "parametric-eq"
    const pluginName = (plugin.name || '').toLowerCase().replace(/\s+/g, '-');
    const pluginParams = getPluginAutomationParams(pluginName);
    
    if (Object.keys(pluginParams).length > 0) {
      // Create unique keys for each plugin instance (in case of multiple same-type plugins)
      const pluginKey = `${plugin.id}`;
      result.plugins[pluginKey] = {
        pluginId: plugin.id,
        pluginName: plugin.name,
        pluginIndex: index,
        pluginType: pluginName,
        params: pluginParams,
      };
    }
  });
  
  return result;
};

/**
 * Create a full parameter key for a plugin parameter
 * Format: pluginInstanceId:pluginType.paramName
 */
export const createPluginParamKey = (pluginId, pluginType, paramName) => {
  return `${pluginId}:${pluginType}.${paramName}`;
};

/**
 * Parse a full plugin parameter key
 */
export const parseFullPluginParamKey = (fullKey) => {
  if (!fullKey.includes(':')) {
    // This is a track-level parameter
    return { isPlugin: false, paramKey: fullKey };
  }
  
  const [pluginId, rest] = fullKey.split(':');
  const [pluginType, paramName] = rest.split('.');
  
  return {
    isPlugin: true,
    pluginId,
    pluginType,
    paramName,
    paramKey: `${pluginType}.${paramName}`,
  };
};

/**
 * Get the config for any parameter (track or plugin)
 */
export const getParamConfig = (parameterKey) => {
  // Check if it's a plugin parameter with instance ID
  if (parameterKey.includes(':')) {
    const parsed = parseFullPluginParamKey(parameterKey);
    return PLUGIN_AUTOMATION_PARAMS[parsed.paramKey];
  }
  
  // Check track params first
  if (AUTOMATION_PARAMS[parameterKey]) {
    return AUTOMATION_PARAMS[parameterKey];
  }
  
  // Check plugin params
  return PLUGIN_AUTOMATION_PARAMS[parameterKey];
};

// Curve types for interpolation
export const CURVE_TYPES = {
  LINEAR: 'linear',
  EXPONENTIAL: 'exponential',
  HOLD: 'hold', // Step/hold - no interpolation
};

/**
 * Create an automation point
 */
export const createAutomationPoint = ({
  time,
  value,
  curveType = CURVE_TYPES.LINEAR,
  id = null,
}) => ({
  id: id || generateId(),
  time,
  value,
  curveType,
});

/**
 * Create an automation lane
 */
export const createAutomationLane = ({
  trackId,
  parameter,
  points = [],
  isVisible = true,
  id = null,
}) => ({
  id: id || generateId(),
  trackId,
  parameter,
  points,
  isVisible,
});

/**
 * Add a point to an automation lane
 */
export const addPointToLane = (lane, point) => {
  const newPoints = [...lane.points, point].sort((a, b) => a.time - b.time);
  return { ...lane, points: newPoints };
};

/**
 * Remove a point from an automation lane
 */
export const removePointFromLane = (lane, pointId) => ({
  ...lane,
  points: lane.points.filter(p => p.id !== pointId),
});

/**
 * Update a point in an automation lane
 */
export const updatePointInLane = (lane, pointId, updates) => ({
  ...lane,
  points: lane.points.map(p => p.id === pointId ? { ...p, ...updates } : p).sort((a, b) => a.time - b.time),
});

/**
 * Get the value at a specific time using interpolation
 */
export const getValueAtTime = (lane, time, paramConfig) => {
  if (!lane || lane.points.length === 0) {
    return paramConfig?.default ?? 0;
  }
  
  const points = lane.points;
  
  // Before first point
  if (time <= points[0].time) {
    return points[0].value;
  }
  
  // After last point
  if (time >= points[points.length - 1].time) {
    return points[points.length - 1].value;
  }
  
  // Find surrounding points
  let prevPoint = points[0];
  let nextPoint = points[points.length - 1];
  
  for (let i = 0; i < points.length - 1; i++) {
    if (time >= points[i].time && time < points[i + 1].time) {
      prevPoint = points[i];
      nextPoint = points[i + 1];
      break;
    }
  }
  
  // Interpolate based on curve type
  const t = (time - prevPoint.time) / (nextPoint.time - prevPoint.time);
  
  switch (prevPoint.curveType) {
    case CURVE_TYPES.HOLD:
      return prevPoint.value;
      
    case CURVE_TYPES.EXPONENTIAL:
      // Exponential interpolation (good for volume fades)
      const expT = Math.pow(t, 2);
      return prevPoint.value + (nextPoint.value - prevPoint.value) * expT;
      
    case CURVE_TYPES.LINEAR:
    default:
      // Linear interpolation
      return prevPoint.value + (nextPoint.value - prevPoint.value) * t;
  }
};

/**
 * Normalize a value to 0-1 range for display
 */
export const normalizeValue = (value, paramConfig) => {
  const { min, max } = paramConfig;
  return (value - min) / (max - min);
};

/**
 * Denormalize a 0-1 value to parameter range
 */
export const denormalizeValue = (normalizedValue, paramConfig) => {
  const { min, max } = paramConfig;
  return min + normalizedValue * (max - min);
};

/**
 * Format value for display
 */
export const formatValue = (value, paramConfig) => {
  if (paramConfig.displayFormatter) {
    return paramConfig.displayFormatter(value);
  }
  
  const displayValue = paramConfig.displayMultiplier 
    ? Math.round(value * paramConfig.displayMultiplier)
    : Math.round(value * 100) / 100;
    
  return `${displayValue}${paramConfig.unit}`;
};

/**
 * Snap time to grid
 */
export const snapTimeToGrid = (time, gridSize, enabled = true) => {
  if (!enabled || !gridSize) return time;
  return Math.round(time / gridSize) * gridSize;
};

/**
 * Get default automation lane for a parameter
 */
export const getDefaultLane = (trackId, parameter) => {
  const paramConfig = getParamConfig(parameter) || AUTOMATION_PARAMS[parameter];
  return createAutomationLane({
    trackId,
    parameter,
    points: [
      createAutomationPoint({ time: 0, value: paramConfig?.default ?? 0 }),
    ],
  });
};

export default {
  AUTOMATION_PARAMS,
  PLUGIN_AUTOMATION_PARAMS,
  CURVE_TYPES,
  createAutomationPoint,
  createAutomationLane,
  addPointToLane,
  removePointFromLane,
  updatePointInLane,
  getValueAtTime,
  normalizeValue,
  denormalizeValue,
  formatValue,
  snapTimeToGrid,
  getDefaultLane,
  getAllAutomationParams,
  getPluginAutomationParams,
  isPluginParameter,
  parsePluginParamKey,
  getTrackAutomationParams,
  createPluginParamKey,
  parseFullPluginParamKey,
  getParamConfig,
};
